'use client'

import { useEffect } from 'react'

/**
 * Rendered only in embed mode (inside the inline embed iframe). Continuously
 * reports the document's content height to the host page via postMessage so the
 * embed loader can size the iframe to fit — giving a seamless, scrollbar-free
 * inline embed instead of a fixed-height scrolly box.
 */
export default function EmbedHeightReporter() {
  useEffect(() => {
    // Only meaningful inside an iframe.
    if (typeof window === 'undefined' || window.parent === window) return

    let last = 0
    let raf = 0
    const post = () => {
      const h = Math.ceil(document.documentElement.getBoundingClientRect().height)
      if (h && Math.abs(h - last) > 1) {
        last = h
        try { window.parent.postMessage({ ptEmbed: true, event: 'height', height: h }, '*') } catch { /* no-op */ }
      }
    }
    const schedule = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(post)
    }

    post()
    const ro = new ResizeObserver(schedule)
    ro.observe(document.documentElement)
    if (document.body) ro.observe(document.body)
    // Catch late async content (fonts, images, data fetches) that ResizeObserver
    // can miss between paints.
    const interval = setInterval(post, 600)
    window.addEventListener('load', post)

    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      clearInterval(interval)
      window.removeEventListener('load', post)
    }
  }, [])

  return null
}
